import React, { useState, useEffect, useRef } from 'react';

// MUI Core components
import { Box, Container, Typography, Grid, IconButton, useTheme, ThemeProvider, createTheme } from '@mui/material';

// MUI Icons
import BarChartIcon from '@mui/icons-material/BarChart';
import ArticleIcon from '@mui/icons-material/Article';
// import ShieldIcon from '@mui/icons-material/Shield';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
// import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
// import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// --- Assets ---
import dashboard1 from 'assets/images/lease/lease-insight.png';
import dashboard2 from 'assets/images/lease/lease-insight-2.png';
import dashboard3 from 'assets/images/lease/lease-list.png';
import dashboard4 from 'assets/images/lease/lease-accounting.png';
// import dashboard5 from 'assets/images/lease/lease-ledger.png';
const originalImages = [dashboard1, dashboard2, dashboard3, dashboard4];

// --- Refined Theme Definition ---
const landingPageTheme = createTheme({
	palette: {
		primary: { main: '#0A192F' },
		secondary: { main: '#020C1B' },
		accent: { main: '#00d1b2' },
		background: { default: '#0A192F' },
		text: {
			primary: '#E6F1FF',
			secondary: '#A8B2D1',
		},
	},
	typography: {
		fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
		h1: { fontWeight: 700, fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', lineHeight: 1.2 },
		h3: { fontWeight: 600, fontSize: '1.1rem' },
		body1: { fontSize: '1.1rem', lineHeight: 1.6 },
	},
});

// --- REFINED ImageSlider with Circular Rotation ---
const ImageSlider = () => {
	const theme = useTheme();

	// Create the cloned slides for the infinite loop effect
	const images = [originalImages[originalImages.length - 1], ...originalImages, originalImages[0]];

	// State to manage the visual transition
	const [isTransitioning, setIsTransitioning] = useState(true);
	// State to manage the current slide index. Start at 1 to show the first "real" image.
	const [currentIndex, setCurrentIndex] = useState(1);

	// Ref to manage the auto-scroll timer
	const intervalRef = useRef(null);

	const startTimer = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		intervalRef.current = setInterval(() => {
			setCurrentIndex((prevIndex) => prevIndex + 1);
		}, 4000);
	};

	// Effect to handle the "jump" when reaching a cloned slide
	useEffect(() => {
		// If we are on a cloned slide, wait for the transition to end, then jump
		if (currentIndex === 0 || currentIndex === images.length - 1) {
			const timer = setTimeout(() => {
				setIsTransitioning(false); // Disable transition for the jump
				// Jump to the corresponding real slide
				setCurrentIndex(currentIndex === 0 ? images.length - 2 : 1);
			}, 500); // This duration should match the CSS transition duration
			return () => clearTimeout(timer);
		}
	}, [currentIndex, images.length]);

	// Effect to re-enable transition after the jump
	useEffect(() => {
		if (!isTransitioning) {
			// A tiny delay to allow the jump to happen before re-enabling transition
			const timer = setTimeout(() => setIsTransitioning(true), 50);
			return () => clearTimeout(timer);
		}
	}, [isTransitioning]);

	// Start and manage the auto-scroll timer
	useEffect(() => {
		startTimer();
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, []);

	const changeSlide = (direction) => {
		startTimer(); // Reset timer on manual click
		setCurrentIndex((prevIndex) => prevIndex + direction);
	};

	const goToSlide = (slideIndex) => {
		startTimer(); // Reset timer on manual click
		setCurrentIndex(slideIndex + 1); // Adjust for the prepended clone
	};

	// Calculate the logical index for the indicators (0, 1, 2)
	const realIndex = (currentIndex - 1 + originalImages.length) % originalImages.length;

	return (
		<Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
			{/* The sliding container */}
			<Box
				sx={{
					display: 'flex',
					height: '100%',
					transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
					transform: `translateX(-${currentIndex * 100}%)`,
				}}
			>
				{images.map((image, index) => (
					<Box
						key={index}
						sx={{
							minWidth: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							background: `radial-gradient(circle, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 90%)`,
						}}
					>
						<Box
							component="img"
							src={image}
							alt={`Lease Dashboard Screenshot ${index}`}
							sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
						/>
					</Box>
				))}
			</Box>

			{/* Arrow Navigation */}
			{/*<IconButton onClick={() => changeSlide(-1)} size="small" sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'white', backgroundColor: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(5px)', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.6)' } }}>*/}
			{/*	<ChevronLeftIcon />*/}
			{/*</IconButton>*/}
			{/*<IconButton onClick={() => changeSlide(1)} size="small" sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: 'white', backgroundColor: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(5px)', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.6)' } }}>*/}
			{/*	<ChevronRightIcon />*/}
			{/*</IconButton>*/}

			{/* Dashed Index Indicator */}
			<Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1 }}>
				{originalImages.map((_, index) => (
					<Box
						key={index}
						component="button"
						onClick={() => goToSlide(index)}
						sx={{
							p: 0,
							border: 'none',
							cursor: 'pointer',
							height: '4px',
							borderRadius: '2px',
							transition: 'all 0.4s ease',
							backgroundColor: realIndex === index ? 'accent.main' : 'rgba(255, 255, 255, 0.4)',
							width: realIndex === index ? 30 : 20,
							'&:hover': {
								backgroundColor: 'rgba(255, 255, 255, 0.6)',
							},
						}}
					/>
				))}
			</Box>
		</Box>
	);
};

const FeatureItem = ({ icon, title }) => {
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, color: 'text.secondary' }}>
			<Box sx={{ color: 'accent.main' }}>{icon}</Box>
			<Typography variant="h3" color="text.primary">
				{title}
			</Typography>
		</Box>
	);
};

// --- Main Lease Management Section ---
const LeaseSection = () => {
	const theme = useTheme();
	return (
		<Box
			sx={{
				height: 'auto',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
				position: 'relative',
				overflow: 'hidden',
				pt: '120px',
				pb: '56px',
			}}
		>
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					height: '100%',
					opacity: 0.1,
					background: 'radial-gradient(circle at 85% 80%, #00d1b233, transparent 40%)',
				}}
			/>

			<Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center', p: 1, m: 1 }}>
				<Box sx={{ mb: 4 }}>
					<Typography variant="h1" color="text.primary">
						Manage Your Leases
						<br /> According to{' '}
						<Box component="span" sx={{ color: 'accent.main' }}>
							IFRS 16 Standards
						</Box>
					</Typography>
					<Typography variant="body1" color="text.secondary" sx={{ maxWidth: '48rem', mx: 'auto', mt: 2 }}>
						Our IFRS 16 solution Lyn-Lease is engineered for precision and compliance, empowering your business to seamlessly manage lease
						contracts as per the latest standards.
					</Typography>
				</Box>

				<Grid container spacing={3} justifyContent="center" sx={{ maxWidth: '60rem', mx: 'auto', mb: 5 }}>
					<Grid item xs={12} sm={6} md={4}>
						<FeatureItem icon={<BarChartIcon />} title="Compliant Data Input" />
					</Grid>

					<Grid item xs={12} sm={12} md={4}>
						<FeatureItem icon={<AutoAwesomeOutlinedIcon />} title="Automated Accounting" />
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<FeatureItem icon={<ArticleIcon />} title="Comprehensive Reports" />
					</Grid>
				</Grid>

				<Box sx={{ width: { xs: '90vw', md: '55vw' }, mx: 'auto' }}>
					<Box
						sx={{
							width: '100%',
							aspectRatio: '16 / 9',
							borderRadius: 3,
							border: '1px solid rgba(255, 255, 255, 0.15)',
							overflow: 'hidden',
							position: 'relative',
							boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
						}}
					>
						<ImageSlider />
					</Box>
				</Box>
			</Container>
		</Box>
	);
};

// --- The Main Exportable Landing Page Component ---
const LeaseManagementSection = () => {
	return (
		<ThemeProvider theme={landingPageTheme}>
			<Box bgcolor="background.default">
				<LeaseSection />
			</Box>
		</ThemeProvider>
	);
};

export default LeaseManagementSection;
